import React, { useState, useEffect } from 'react';
import { getStatusOptions, getPriorityOptions, CRM_CONSTANTS, calculateDueDate, formatFriendlyDate } from '../../utils/crmConstants';
import { crmDataService } from '../../utils/crmDataService';

function TicketControlsPanel({ ticket, teams, stores, onUpdate }) {
  const [isExpanded, setIsExpanded] = useState(false);

  const [formData, setFormData] = useState({
    status: '',
    priority: '',
    store_pc: '',
    assigned_to_email: '',
    assigned_to_name: '',
    tags: []
    // Escalate feature disabled
    // escalated: false,
    // escalation_reason: ''
  });

  const [originalData, setOriginalData] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [newTag, setNewTag] = useState('');
  const [teamMembers, setTeamMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);

  // Fetch team members when ticket loads
  useEffect(() => {
    const fetchTeamMembers = async () => {
      if (!ticket?.team_code) return;

      setLoadingMembers(true);
      try {
        const members = await crmDataService.getTeamMembers(ticket.team_code);
        setTeamMembers(members || []);
      } catch (err) {
        console.error('Failed to fetch team members:', err);
        setTeamMembers([]);
      } finally {
        setLoadingMembers(false);
      }
    };

    fetchTeamMembers();
  }, [ticket?.team_code]);

  useEffect(() => {
    if (ticket) {
      const initialData = {
        status: ticket.status || CRM_CONSTANTS.TICKET_STATUS.NEW.value,
        priority: ticket.priority || CRM_CONSTANTS.TICKET_PRIORITY.NORMAL.value,
        store_pc: ticket.store_pc || '',
        assigned_to_email: ticket.assigned_to_email || '',
        assigned_to_name: ticket.assigned_to_name || '',
        tags: ticket.tags || []
        // Escalate feature disabled
        // escalated: ticket.escalated || false,
        // escalation_reason: ticket.escalation_reason || ''
      };
      setFormData(initialData);
      setOriginalData(initialData);
      setHasChanges(false);
    }
  }, [ticket]);

  // Check if form has changes compared to original data
  const checkForChanges = (newData) => {
    if (!originalData) return false;
    return JSON.stringify(newData) !== JSON.stringify(originalData);
  };

  const handleChange = (field, value) => {
    const newFormData = { ...formData, [field]: value };
    setFormData(newFormData);
    setHasChanges(checkForChanges(newFormData));
  };

  const handleSave = async () => {
    if (!hasChanges) return;

    setSaving(true);
    try {
      // Prepare update data - only send changed fields
      const updates = {};
      Object.keys(formData).forEach(key => {
        if (JSON.stringify(formData[key]) !== JSON.stringify(originalData[key])) {
          updates[key] = formData[key];
        }
      });

      await onUpdate(updates);

      // Update original data to current form data
      setOriginalData(formData);
      setHasChanges(false);
      setLastSaved(new Date());
    } catch (err) {
      console.error('Failed to update:', err);
      alert('Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    if (!originalData) return;
    setFormData(originalData);
    setHasChanges(false);
  };

  const handleAddTag = () => {
    if (newTag && !formData.tags.includes(newTag)) {
      const newTags = [...formData.tags, newTag];
      handleChange('tags', newTags);
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    const newTags = formData.tags.filter(tag => tag !== tagToRemove);
    handleChange('tags', newTags);
  };

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleExpand();
    }
  };

  if (!ticket) return null;

  return (
    <div className={`ticket-controls-panel ${isExpanded ? 'expanded' : 'collapsed'}`}>
      <div
        className="panel-header collapsible-header"
        onClick={toggleExpand}
        onKeyDown={handleKeyPress}
        tabIndex={0}
        role="button"
        aria-expanded={isExpanded}
        aria-label="Toggle ticket controls"
      >
        <div className="header-left">
          <h2 className="panel-title">Ticket Controls</h2>
        </div>
        <button
          className="collapse-toggle"
          aria-label={isExpanded ? 'Click to collapse' : 'Click to expand'}
          title={isExpanded ? 'Click to collapse' : 'Click to expand'}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={`feather feather-chevron-down ${isExpanded ? 'expanded' : ''}`}
          >
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </button>
      </div>

      {isExpanded && (
        <div className="panel-content">
          <div className="controls-form">
            {/* Horizontal 7-Column Grid Layout */}
            <div className="controls-horizontal-grid">
              {/* Column 1: Status */}
              <div className="control-group">
                <label>Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => handleChange('status', e.target.value)}
                  className="control-select"
                >
                  {getStatusOptions().map(status => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Column 2: Priority */}
              <div className="control-group">
                <label>Priority</label>
                <select
                  value={formData.priority}
                  onChange={(e) => handleChange('priority', e.target.value)}
                  className="control-select"
                >
                  {getPriorityOptions().map(priority => (
                    <option key={priority.value} value={priority.value}>
                      {priority.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Column 3: Store */}
              <div className="control-group">
                <label>Store</label>
                <select
                  value={formData.store_pc}
                  onChange={(e) => handleChange('store_pc', e.target.value)}
                  className="control-select"
                >
                  <option value="">Select Store</option>
                  {stores && [...stores]
                    .sort((a, b) => a.store_name.localeCompare(b.store_name))
                    .map(store => (
                      <option key={store.store_pc} value={store.store_pc}>
                        {store.store_name}
                      </option>
                    ))}
                </select>
              </div>

              {/* Column 4: Assigned To */}
              <div className="control-group">
                <label>Assigned To</label>
                <select
                  value={formData.assigned_to_email}
                  onChange={(e) => {
                    const selectedEmail = e.target.value;
                    const selectedMember = teamMembers.find(m => m.member_email === selectedEmail);

                    // Update both fields in a single state update to avoid async race condition
                    const newFormData = {
                      ...formData,
                      assigned_to_email: selectedEmail,
                      assigned_to_name: selectedMember ? selectedMember.member_name : ''
                    };
                    setFormData(newFormData);
                    setHasChanges(checkForChanges(newFormData));
                  }}
                  className="control-select"
                  disabled={loadingMembers}
                >
                  <option value="">
                    {loadingMembers ? 'Loading members...' : 'Select Team Member'}
                  </option>
                  {teamMembers.map(member => (
                    <option
                      key={member.member_email}
                      value={member.member_email}
                    >
                      {member.member_name} ({member.member_email})
                    </option>
                  ))}
                </select>
                {formData.assigned_to_email && (
                  <span className="assigned-indicator">
                    ✓ Assigned to {formData.assigned_to_email}
                  </span>
                )}
              </div>

              {/* Column 5: Due Date - Read-only calculated field */}
              <div className="control-group">
                <label>Due Date</label>
                <input
                  type="text"
                  value={formatFriendlyDate(calculateDueDate(ticket?.created_at))}
                  readOnly
                  className="control-input"
                  style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                />
                {ticket?.created_at && calculateDueDate(ticket.created_at) < new Date() && (
                  <span className="overdue-indicator">OVERDUE</span>
                )}
              </div>

              {/* Column 6: Tags/Labels */}
              <div className="control-group">
                <label>Tags/Labels</label>
                <div className="tags-input-wrapper">
                  <input
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                    placeholder="Add tag"
                    className="control-input"
                  />
                  <button onClick={handleAddTag} className="btn-add-tag">+</button>
                </div>
                <div className="tags-list">
                  {formData.tags && formData.tags.map((tag, index) => (
                    <span key={index} className="tag-chip">
                      {tag}
                      <button onClick={() => handleRemoveTag(tag)} className="tag-remove">×</button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Column 7: Escalate Checkbox - DISABLED */}
              {false && (
                <div className="control-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.escalated}
                      onChange={(e) => handleChange('escalated', e.target.checked)}
                      className="control-checkbox"
                    />
                    <span>Escalate</span>
                  </label>
                </div>
              )}
            </div>

            {/* Row 2: Escalation Textarea - DISABLED */}
            {false && formData.escalated && (
              <div className="escalation-row">
                <div className="control-group">
                  <label>Escalation Reason</label>
                  <textarea
                    value={formData.escalation_reason}
                    onChange={(e) => handleChange('escalation_reason', e.target.value)}
                    placeholder="Escalation reason..."
                    className="control-textarea"
                    rows="3"
                  />
                </div>
              </div>
            )}

            {/* Row 3: Save/Discard buttons */}
            <div className="control-actions">
              <button
                onClick={handleSave}
                disabled={!hasChanges || saving}
                className={`btn-save ${hasChanges ? 'btn-save-enabled' : ''}`}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                onClick={handleDiscard}
                disabled={!hasChanges || saving}
                className="btn-discard"
              >
                Discard Changes
              </button>
              {lastSaved && !hasChanges && (
                <span className="saved-indicator">
                  Last saved at {lastSaved.toLocaleTimeString()}
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TicketControlsPanel;
